import { useCallback, useEffect, useRef, useState } from 'react';
import { Project, VATRate } from '@bulthaup/api/db';
import { getProjectAmounts } from '@bulthaup/api/entities/budget';
import { sum } from '@bulthaup/api/utils';
import {
  Button,
  Card,
  Divider,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCalculator } from '@tabler/icons-react';
import { Decimal } from 'decimal.js';
import { isEqual, pick } from 'lodash';

import { trpc } from '../../../../../../services/trpc';
import { formatAmount, formatPercentage } from '../../../../../../utils/format';
import { EcotaxAlert } from './-ecotax-alert';
import { TotalAmountAlert } from './-total-amount-alert';

const FIELDS_TO_COMPARE = [
  'furnitureDiscountPercentage',
  'applianceDiscountPercentage',
  'furniture10PercentAmount',
  'furniture10PercentEcofeeAmount',
  'faucets10PercentAmount',
  'faucets10PercentEcofeeAmount',
  'deliveryAndInstallation10PercentAmount',
  'furnitureAmount',
  'furnitureEcofeeAmount',
  'applianceAmount',
  'applianceEcofeeAmount',
  'faucetsAmount',
  'faucetsEcofeeAmount',
  'deliveryAndInstallationAmount',
  'miscellaneousAmount',
  'vatRate',
  'architectFeePercent',
  'architectFeeAmount',
] as const;

const VAT_RATE_OPTIONS = [
  { value: VATRate.TWENTY_PERCENT, label: '20%' },
  { value: VATRate.TEN_PERCENT, label: '10%' },
  { value: VATRate.EIGHT_PERCENT, label: '8%' },
  { value: VATRate.FOREIGN, label: 'Étranger' },
];

export function BudgetSection({ project }: { project: Project }) {
  const utils = trpc.useUtils();

  // Convert Decimal to number for local state
  const initialValues = pick(project, FIELDS_TO_COMPARE);
  const convertedInitialValues = Object.fromEntries(
    Object.entries(initialValues).map(([key, value]) => [
      key,
      value instanceof Decimal ? Number(value) : value,
    ]),
  ) as Record<string, number | VATRate | null>;

  const [localValues, setLocalValues] = useState(convertedInitialValues);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { gross, architectFee, amounts, discountedAmounts } =
    getProjectAmounts(project);
  const totalGross = sum(Object.values(gross));
  const totalHT = sum(Object.values(amounts));
  const totalDiscountedHT = sum(Object.values(discountedAmounts));
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    appliance,
    ...amountsWithoutAppliance
  } = amounts;

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    appliance: discountedAppliance,
    ...discountedAmountsWithoutAppliance
  } = discountedAmounts;

  const updateBudget = trpc.projects.updateBudget.useMutation({
    onSuccess: () => {
      utils.projects.get.invalidate({ slug: project.slug });
    },
    onError: error => {
      notifications.show({
        title: 'Erreur',
        message: error.message,
        color: 'red',
      });
    },
  });

  const handleChange = (
    field: string,
    value: number | string | null | VATRate,
  ) => {
    const newValues = {
      ...localValues,
      [field]: value,
    } as typeof localValues;

    setLocalValues(newValues);
    const hasChanges = !isEqual(
      pick(newValues, FIELDS_TO_COMPARE),
      pick(project, FIELDS_TO_COMPARE),
    );

    // Trigger auto-save only if there are changes
    if (hasChanges) {
      debouncedAutoSave(newValues);
    }
  };

  const performSave = useCallback(
    async (values: typeof localValues) => {
      const dataToSave = Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          value === null && key !== 'vatRate' ? 0 : value,
        ]),
      );

      await updateBudget.mutateAsync({
        id: project.id,
        ...dataToSave,
      });
    },
    [project.id, updateBudget],
  );

  const debouncedAutoSave = useCallback(
    (values: typeof localValues) => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      autoSaveTimeoutRef.current = setTimeout(() => {
        if (
          !isEqual(
            pick(values, FIELDS_TO_COMPARE),
            pick(project, FIELDS_TO_COMPARE),
          )
        ) {
          performSave(values);
        }
      }, 1000); // Auto-save after 1 second of inactivity
    },
    [performSave, project],
  );

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Budget</Title>
      </Group>
      <Group gap="md" style={{ maxWidth: 500, alignItems: 'stretch' }}>
        <Paper p="md" withBorder style={{ flex: 1 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Total avant réductions
            </Text>

            <Text size="lg" fw={700}>
              {formatAmount(totalHT)}
            </Text>
          </Stack>
        </Paper>
        <Paper p="md" withBorder style={{ flex: 1 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Total HT
            </Text>
            <Text size="lg" fw={700}>
              {formatAmount(totalDiscountedHT)}
            </Text>
          </Stack>
        </Paper>
        <Paper p="md" withBorder style={{ flex: 1 }}>
          <Stack gap={4}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Total TTC
            </Text>
            <Text size="md" fw={600} c="dark.4">
              {formatAmount(totalGross)}
            </Text>
          </Stack>
        </Paper>
      </Group>
      <Card>
        <Stack gap="md">
          <Grid gutter="md">
            {/* Variable VAT Section */}
            <Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
              <Stack style={{ minWidth: '280px' }}>
                <Paper p="md" withBorder>
                  <Stack gap="sm">
                    <Text fw={700} size="lg" c="dark">
                      TVA 10%
                    </Text>

                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Meuble 10%
                      </Text>
                      <NumberInput
                        description="Montant HT"
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.furniture10PercentAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'furniture10PercentAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.furniture10PercentEcofeeAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'furniture10PercentEcofeeAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                        description="Écotaxe"
                      />
                    </Group>
                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Robinetterie 10%
                      </Text>
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.faucets10PercentAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'faucets10PercentAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.faucets10PercentEcofeeAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'faucets10PercentEcofeeAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                    </Group>

                    <Group align="center" justify="start">
                      <Text size="sm" style={{ flex: 1 }}>
                        Livraison et Pose 10%
                      </Text>
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={
                          localValues.deliveryAndInstallation10PercentAmount ??
                          ''
                        }
                        onChange={value =>
                          handleChange(
                            'deliveryAndInstallation10PercentAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                      <div style={{ width: 120 }} />
                    </Group>
                  </Stack>
                </Paper>
                <Paper p="md" withBorder>
                  <Stack gap="sm">
                    <Text fw={700} size="lg" c="dark">
                      TVA
                    </Text>

                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Taux TVA
                      </Text>
                      <Select
                        data={VAT_RATE_OPTIONS}
                        value={localValues.vatRate?.toString() ?? ''}
                        onChange={value => handleChange('vatRate', value)}
                        style={{ width: 120 }}
                        allowDeselect
                      />
                    </Group>
                    <Divider />

                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Meuble
                      </Text>
                      <NumberInput
                        description="Montant HT"
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.furnitureAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'furnitureAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                      <NumberInput
                        description="Écotaxe"
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.furnitureEcofeeAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'furnitureEcofeeAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                    </Group>

                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Électro
                      </Text>
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.applianceAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'applianceAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.applianceEcofeeAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'applianceEcofeeAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                    </Group>
                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Robinetterie
                      </Text>
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.faucetsAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'faucetsAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.faucetsEcofeeAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'faucetsEcofeeAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                    </Group>
                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Livraison et Pose
                      </Text>
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.deliveryAndInstallationAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'deliveryAndInstallationAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                      <div style={{ width: 120 }} />
                    </Group>
                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Divers
                      </Text>
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.miscellaneousAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'miscellaneousAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                      <div style={{ width: 120 }} />
                    </Group>
                  </Stack>
                </Paper>
              </Stack>
            </Grid.Col>

            {/* Discounts Section */}
            <Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
              <Stack gap="md" style={{ minWidth: '280px' }}>
                <Paper p="md" withBorder>
                  <Stack gap="sm">
                    <Text fw={700} size="lg" c="dark">
                      Réductions
                    </Text>

                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Réduction Meuble
                      </Text>
                      <Stack gap="xs" align="flex-end">
                        <Group gap="xs">
                          <NumberInput
                            min={0}
                            max={100}
                            rightSection="%"
                            step={0.01}
                            decimalScale={8}
                            value={
                              localValues.furnitureDiscountPercentage ?? ''
                            }
                            onChange={value =>
                              handleChange(
                                'furnitureDiscountPercentage',
                                value !== null && value !== ''
                                  ? Number(value)
                                  : null,
                              )
                            }
                            style={{ width: 100 }}
                          />
                          <Button
                            variant="outline"
                            color="gray"
                            size="compact-lg"
                            style={{
                              height: 36,
                              paddingLeft: 6,
                              paddingRight: 6,
                            }}
                            onClick={() => {
                              let afterAmount: Decimal | null = null;
                              try {
                                const afterAmountString = window.prompt(
                                  'Inscrivez le montant total meuble HT remisé',
                                );

                                if (!afterAmountString) return;

                                afterAmount = new Decimal(
                                  afterAmountString.trim().replace(',', '.'),
                                );
                              } catch {
                                window.alert(
                                  'Le montant total meuble HT remisé doit être un nombre',
                                );
                                return;
                              }

                              const beforeAmount = sum(
                                Object.values(amountsWithoutAppliance),
                              );
                              const discountPercentage = new Decimal(
                                beforeAmount,
                              )
                                .sub(afterAmount)
                                .mul(100)
                                .div(beforeAmount);

                              handleChange(
                                'furnitureDiscountPercentage',
                                discountPercentage.toNumber(),
                              );
                            }}
                          >
                            <IconCalculator size={20} stroke={1.5} />
                          </Button>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {`Soit : ${formatAmount(
                            sum(Object.values(amountsWithoutAppliance)).sub(
                              sum(
                                Object.values(
                                  discountedAmountsWithoutAppliance,
                                ),
                              ),
                            ),
                          )} sur un total de ${formatAmount(
                            sum(Object.values(amountsWithoutAppliance)),
                          )} HT`}
                        </Text>
                      </Stack>
                    </Group>

                    <Group align="center">
                      <Text size="sm" style={{ flex: 1 }}>
                        Réduction Électro
                      </Text>
                      <Stack gap="xs" align="flex-end">
                        <Group gap="xs">
                          <NumberInput
                            min={0}
                            max={100}
                            rightSection="%"
                            step={0.01}
                            decimalScale={8}
                            value={
                              localValues.applianceDiscountPercentage ?? ''
                            }
                            onChange={value =>
                              handleChange(
                                'applianceDiscountPercentage',
                                value !== null && value !== ''
                                  ? Number(value)
                                  : null,
                              )
                            }
                            style={{ width: 100 }}
                          />
                          <Button
                            variant="outline"
                            color="gray"
                            size="compact-lg"
                            style={{
                              height: 36,
                              paddingLeft: 6,
                              paddingRight: 6,
                            }}
                            onClick={() => {
                              let afterAmount: Decimal | null = null;
                              try {
                                const afterAmountString = window.prompt(
                                  'Inscrivez le montant total électro HT remisé',
                                );

                                if (!afterAmountString) return;

                                afterAmount = new Decimal(
                                  afterAmountString.trim().replace(',', '.'),
                                );
                              } catch {
                                window.alert(
                                  'Le montant total meuble HT remisé doit être un nombre',
                                );
                                return;
                              }
                              if (!afterAmount) return;

                              const beforeAmount = amounts.appliance;
                              const discountPercentage = new Decimal(
                                beforeAmount,
                              )
                                .sub(afterAmount)
                                .mul(100)
                                .div(beforeAmount);

                              handleChange(
                                'applianceDiscountPercentage',
                                discountPercentage.toNumber(),
                              );
                            }}
                          >
                            <IconCalculator size={20} stroke={1.5} />
                          </Button>
                        </Group>
                        <Text size="xs" c="dimmed">
                          {`Soit : ${formatAmount(
                            amounts.appliance.sub(discountedAmounts.appliance),
                          )} sur un total de ${formatAmount(amounts.appliance)} HT`}
                        </Text>
                      </Stack>
                    </Group>
                  </Stack>
                </Paper>
                <Paper p="md" withBorder>
                  <Stack gap="sm">
                    <Text fw={700} size="lg" c="dark">
                      Honoraires
                    </Text>

                    <Group align="center" justify="space-between">
                      <Stack gap="xs">
                        <Text size="sm" style={{ flex: 1 }}>
                          Honoraires Architecte
                        </Text>
                        <Text size="xs" c="dimmed">
                          Calculé à partir de la ligne Meuble Bulthaup
                          <br />
                          sur la fiche fournisseur
                        </Text>
                      </Stack>

                      {/* Input - Architect Fee in Percent */}
                      {/* <NumberInput
                        min={0}
                        max={100}
                        rightSection="%"
                        step={0.01}
                        decimalScale={2}
                        value={localValues.architectFeePercent ?? ''}
                        onChange={value =>
                          handleChange(
                            'architectFeePercent',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 100 }}
                      />
                      <Stack gap="xs">
                        <Text size="xs" c="dimmed">
                          Soit : {formatAmount(architectFee)} HT /{' '}
                          {formatAmount(gross.architectFee)} TTC
                        </Text>
                      </Stack> */}

                      {/* Input - Architect Fee in Amount */}
                      <NumberInput
                        min={0}
                        decimalScale={2}
                        thousandSeparator=" "
                        rightSection="€"
                        step={0.01}
                        value={localValues.architectFeeAmount ?? ''}
                        onChange={value =>
                          handleChange(
                            'architectFeeAmount',
                            value !== null && value !== ''
                              ? Number(value)
                              : null,
                          )
                        }
                        style={{ width: 120 }}
                      />
                      <Stack gap="xs">
                        <Text size="xs" c="dimmed">
                          Soit :{' '}
                          {formatPercentage(project.architectFeePercent ?? 0)}
                        </Text>
                      </Stack>
                    </Group>
                  </Stack>
                </Paper>
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
              <Stack gap="md" style={{ minWidth: '280px' }}>
                <EcotaxAlert project={project} />
                <TotalAmountAlert project={project} />
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Card>
    </Stack>
  );
}
